import React, { useState, useEffect } from "react";
import { Box, Typography, Link, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AuthenticationLeftContent from "../../../components/common/AutenticationLeftContent";
import AuthenticationTextInput from "../../../components/common/TextInput/AuthenticationTextInput";
import PrimaryButton from "../../../components/common/PrimaryButton";
import Heading from "../../../components/common/AutenticationTitles/Heading";
import Subtitle from "../../../components/common/AutenticationTitles/Subtitle";
import { useDispatch, useSelector } from "react-redux";
import { verifyCodeAction, forgotPasswordAction } from "../../../store/slices/authSlice";
import { useLocation, useNavigate } from "react-router-dom";

const VerificationPage = ({ onClose, onViewChange, isMobile = false }) => {
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(30);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const emailFromRoute = location.state?.email;
  const resetEmail = useSelector((state) => state.auth.resetEmail);

  // Timer countdown effect
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length <= 6) {
      setCode(value);
      setError(""); // Clear error when user types
    }
  };

  const handleResendCode = async () => {
    const email = emailFromRoute || resetEmail;
    if (!email) {
      setError("Email missing. Please start from Forgot Password.");
      return;
    }
    
    if (timer > 0 || isResending) return;
    
    try {
      setIsResending(true);
      setError("");
      const result = await dispatch(forgotPasswordAction(email));
      if (forgotPasswordAction.fulfilled.match(result)) {
        // Reset timer after successful resend
        setTimer(30);
      } else if (forgotPasswordAction.rejected.match(result)) {
        // Handle error from forgot password API
        const errorMessage = result.payload || result.error?.message || "Failed to resend code. Please try again.";
        const errorLower = errorMessage.toLowerCase();
        if (errorLower.includes("user not found") || 
            errorLower.includes("no user found") || 
            errorLower.includes("user does not exist") ||
            errorLower.includes("email not found")) {
          setError("No user found with this email");
        } else {
          setError(errorMessage);
        }
      }
    } catch (error) {
      console.error("Error resending code:", error);
      setError(error?.response?.data?.message || "Failed to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors
    if (!code) {
      setError("Verification code is required");
      return;
    }
    if (code.length !== 6) {
      setError("Code must be 6 digits");
      return;
    }
    const email = emailFromRoute || resetEmail;
    if (!email) {
      setError("Email missing. Please start from Forgot Password.");
      return;
    }
    try {
      const result = await dispatch(verifyCodeAction({ email, code }));
      if (verifyCodeAction.fulfilled.match(result)) {
        navigate("/reset-password", { state: { email, code } });
      } else if (verifyCodeAction.rejected.match(result)) {
        // Extract error message from API response
        // Handle both error formats: { message: "..." } and { errors: [{ msg: "..." }] }
        let errorMessage = "Invalid or expired verification code";
        
        if (result.payload) {
          // Check for errors array format
          if (Array.isArray(result.payload.errors) && result.payload.errors.length > 0) {
            errorMessage = result.payload.errors[0].msg || errorMessage;
          } 
          // Check for message format
          else if (result.payload.message) {
            errorMessage = result.payload.message;
          }
          // If payload is a string
          else if (typeof result.payload === 'string') {
            errorMessage = result.payload;
          }
        } else if (result.error?.message) {
          errorMessage = result.error.message;
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      setError(err?.message || "An error occurred. Please try again.");
    }
  };

  return (
    <Box
      style={{ 
      display: "flex", 
      flexDirection: isMobile ? "column" : isTablet ? "column" : "row",
      backgroundColor: "#FFFFFF", 
      borderRadius: isMobile ? "12px" : isTablet ? "12px" : "22px",
      position: "relative", 
      padding: isMobile ? "0px" : isTablet ? "0px" : "20px 0px 20px 20px",
      height: isMobile ? "100vh" : isTablet ? "100vh" : "auto",
      overflow: isMobile ? "hidden" : isTablet ? "hidden" : "visible"
    }}
    >
      <Box className="left-width" flex={1} sx={{maxWidth:isMobile ? "100%": isTablet ? "100%" : isLaptop? "300px" :"499px"}}>
        <AuthenticationLeftContent onClose={onClose} />
      </Box>
      <Box className="right-content" flex={1} p={isMobile ? 2 : isTablet ? 2 : 4} sx={{
        maxWidth:isMobile?"100%":isLaptop?"390px": "100%",
        marginInline: isMobile ? "0" : isTablet ? "0" : "auto",
        height: isMobile ? "50vh" : isTablet ? "60vh" : "auto",
        overflow: isMobile ? "auto" : isTablet ? "auto" : "visible",
        display: "flex",
        flexDirection: "column",
        justifyContent: isMobile ? "flex-start" : "center",
        width: isMobile ? "100%" : isTablet ? "100%" : "auto",
        position: "relative",
        borderRadius: isMobile ? "0 0 12px 12px" : isTablet ? "0 0 12px 12px" : "0"
      }}>
        <Box style={{ display: "flex",justifyContent: "space-between", alignItems: "end" }}>
          <Box>
            <Heading>Verify your account</Heading>
            <Subtitle>Enter the verification code sent to your email</Subtitle>
          </Box>
          <Box  display="flex" justifyContent="flex-end" style={{ marginBottom: isMobile ? "20px" : "30px" }}>
            {timer > 0 ? (
              <Typography
                style={{
                  color: "#5E6366",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {`00:${timer.toString().padStart(2, "0")}`}
              </Typography>
            ) : (
              <Link
                href="#"
                underline="none"
                style={{
                  color: "#F04951",
                  fontWeight: 700,
                  fontSize: "14px",
                  cursor: isResending ? "not-allowed" : "pointer",
                  opacity: isResending ? 0.7 : 1,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleResendCode();
                }}
              >
                {isResending ? "Sending..." : "Resend Code"}
              </Link>
            )}
          </Box>
        </Box>
        <form onSubmit={handleSubmit}>
          <AuthenticationTextInput
            label=""
            value={code}
            onChange={handleCodeChange}
            placeholder="Verification code"
            error={error}
            helperText={error}
            inputProps={{ maxLength: 6 }}
          />
          <PrimaryButton>Submit</PrimaryButton>
        </form>
        <Link
          href="#"
          underline="none"
          onClick={(e) => {
            e.preventDefault();
            if (onViewChange) onViewChange("login");
          }}
          style={{
            fontSize: isMobile ? "14px" : isTablet ? "14px" : "16px",
            fontWeight: "700",
            color: "#141414",
            cursor: "pointer",
            textAlign: "center",
            display: "block",
            marginTop: isMobile ? "16px" : isTablet ? "8px" : "24px",
          }}
        >
          Back to Login
        </Link>
      </Box>
    </Box>
  );
};

export default VerificationPage;
